import { Injectable } from "@angular/core";
import {
  ActivatedRouteSnapshot,
  Router,
  RouterStateSnapshot,
} from "@angular/router";
import {
  filter,
  finalize,
  map,
  Observable,
  race,
  take,
  tap,
  timer,
} from "rxjs";
import { AccountService } from "src/app/modules/authentication/services/account.service";
import { AuthCoordinatorService } from "src/app/modules/authentication/services/auth-coordinator.service";
import { AppService } from "src/services/app.service";
import { PAGE_URLS } from "../models/general";

@Injectable({ providedIn: "root" })
export class AuthGuard {
  constructor(
    private authCoordinatorService: AuthCoordinatorService,
    private accountService: AccountService,
    private appService: AppService,
    private router: Router,
  ) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean> | boolean {
    this.appService.setIsAuthGuardNavigationPending(false);
    // Case 1: User is already authenticated
    if (this.accountService.isLoggedIn()) {
      return true;
    }

    // Save the attempted URL to redirect after login
    this.appService.setReturnUrl(state.url);

    // Case 2: Attempt to restore session via refresh token
    if (this.accountService.canRestoreSessionFromRefreshToken()) {
      return this.waitForAuthOrTimeout(state.url);
    }

    // No session or refresh token, redirecting
    this.redirectToLogin(state.url);
    return false;
  }

  private waitForAuthOrTimeout(returnUrl: string): Observable<boolean> {
    const TIMEOUT_MS = 3000;
    this.appService.setIsAuthGuardNavigationPending(true);
    return race(
      // Case A: another tab (leader) completes refresh and notifies this one
      this.appService.getLoggedInObservable().pipe(filter(Boolean), take(1)),

      // Case B: this tab is leader and completes refresh
      this.authCoordinatorService.refreshTokenSubject$.pipe(
        filter((token) => token !== null),
        take(1),
        map(Boolean),
      ),

      // Case C: Timeout fallback
      timer(TIMEOUT_MS).pipe(map(() => false)),
    ).pipe(
      take(1),
      tap((isAuthenticated) => {
        this.appService.setIsAuthGuardNavigationPending(false);
        if (!isAuthenticated) {
          this.redirectToLogin(returnUrl);
        }
      }),
      finalize(() => {
        this.appService.setIsAuthGuardNavigationPending(false);
      }),
    );
  }

  private redirectToLogin(returnUrl: string): void {
    this.router.navigate([PAGE_URLS.LOGIN], {
      queryParams: { returnUrl },
    });
  }
}
